import React from 'react'
import { useApp } from '../context/AppContext'
import { api } from '../api/client'
import type { Appointment } from '../api/client'
import { formRow, sendFormMessage } from '../forms/status'
import FormSubmissionSheet from '../screens/FormSubmissionSheet'
import { showToast } from '../screens/settings/toast'
import '../screens/ClientForms.css'

interface Props {
  appointment: Appointment
  // Told when a form screen is open over the sheet, so Escape leaves it alone.
  onOverlayChange: (open: boolean) => void
}

// AppointmentForm is the sheet's form row, for the form of the booked
// service: a dot and where the form stands, then the one thing to do about
// it (Send form while it's unsigned, View answers once it's signed).
export default function AppointmentForm({ appointment: a, onOverlayChange }: Props) {
  const { dispatch } = useApp()
  const [templateId, setTemplateId] = React.useState<string | null>(null)
  const [sending, setSending] = React.useState(false)
  const [error, setError] = React.useState('')
  const [submissionId, setSubmissionId] = React.useState<string | null>(null)

  const clientId = a.client?.id ?? null
  const serviceId = a.service.id

  // The appointment carries the status, not the form: the service knows which.
  React.useEffect(() => {
    let live = true
    setTemplateId(null)
    if (!serviceId) return
    api
      .listServices()
      .then((services) => {
        if (live) setTemplateId(services.find((s) => s.id === serviceId)?.form_template_id ?? null)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [serviceId])

  const overlay = submissionId !== null
  React.useEffect(() => {
    onOverlayChange(overlay)
    return () => onOverlayChange(false)
  }, [overlay, onOverlayChange])

  const row = formRow(a.form_status, a.form_signed_at, new Date())
  if (!clientId || !row) return null
  const first = a.client?.first_name ?? 'the client'

  // View answers opens the newest signed copy.
  const openLatest = async () => {
    if (!templateId) return
    setError('')
    try {
      const forms = await api.listClientForms(clientId)
      const latest = forms.find((f) => f.template_id === templateId)?.submissions[0]
      if (latest) setSubmissionId(latest.id)
    } catch (e) {
      setError(sendFormMessage(e))
    }
  }

  const send = async () => {
    if (!templateId || sending) return
    setSending(true)
    setError('')
    try {
      await api.sendClientForm(clientId, templateId)
      showToast(dispatch, `Form emailed to ${first}`)
    } catch (e) {
      setError(sendFormMessage(e))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="appt-detail-item appt-form-row">
      <span className="detail-label">Form</span>
      <span className="detail-value">
        <span className={`form-dot form-dot--${row.tone}`} aria-hidden="true" />
        {row.text}
      </span>
      {row.action === 'view' ? (
        <button type="button" className="btn btn-quiet btn-compact appt-form-row__action" disabled={!templateId} onClick={openLatest}>
          View answers
        </button>
      ) : (
        <button type="button" className="btn btn-secondary btn-compact appt-form-row__action" disabled={!templateId || sending} onClick={send}>
          {row.action === 'send-again' ? 'Send again' : 'Send form'}
        </button>
      )}
      {error && (
        <p className="field-error appt-form-row__error" role="alert">
          {error}
        </p>
      )}
      {submissionId && <FormSubmissionSheet submissionId={submissionId} clientId={clientId} onClose={() => setSubmissionId(null)} />}
    </div>
  )
}
